import { getApiBaseUrl } from '../config/runtime';

const API_BASE_URL = getApiBaseUrl();

export interface AdminCredentials {
    adminUsername: string;
    adminPassword: string;
}

export interface AuctionBidResult {
    firstUser: string;
    firstBid: number;
    secondUser: string;
    secondBid: number;
}

export interface SetStateResult {
    message: string;
    newState: number;
}

export interface MessagePayload {
    errorCode: number;
    errorMessage: string;
}

export interface SetTickersResult {
    message: MessagePayload;
    tickers: string[] | null;
}

export interface SetMaxOrderPriceResult {
    message: MessagePayload;
    maxPrice: number;
}

export interface SendUserWsMessageResult {
    message: string;
}

let storedCredentials: AdminCredentials | null = null;

export function getStoredAdminCredentials(): AdminCredentials | null {
    if (storedCredentials) return storedCredentials;
    const raw = sessionStorage.getItem('adminCredentials');
    if (raw) {
        storedCredentials = JSON.parse(raw);
        return storedCredentials;
    }
    return null;
}

export function storeAdminCredentials(creds: AdminCredentials) {
    storedCredentials = creds;
    sessionStorage.setItem('adminCredentials', JSON.stringify(creds));
}

export function clearAdminCredentials() {
    storedCredentials = null;
    sessionStorage.removeItem('adminCredentials');
}

async function adminPost<T>(
    path: string,
    extraBody?: Record<string, unknown>,
): Promise<{ ok: boolean; status: number; data: T }> {
    const creds = getStoredAdminCredentials();
    if (!creds) {
        return {
            ok: false,
            status: 401,
            data: { message: 'No admin credentials' } as unknown as T,
        };
    }

    const resp = await fetch(API_BASE_URL + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...creds, ...extraBody }),
    });

    let json: any;
    try {
        json = await resp.json();
    } catch {
        json = { message: 'Non-JSON response' };
    }

    return { ok: resp.ok, status: resp.status, data: json as T };
}

async function adminPostText(
    path: string,
    extraBody?: Record<string, unknown>,
): Promise<{ ok: boolean; status: number; data: string }> {
    const creds = getStoredAdminCredentials();
    if (!creds) {
        return { ok: false, status: 401, data: 'No admin credentials' };
    }

    const resp = await fetch(API_BASE_URL + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...creds, ...extraBody }),
    });

    let text = '';
    try {
        text = await resp.text();
    } catch {
        text = '';
    }

    return { ok: resp.ok, status: resp.status, data: text };
}

export async function adminLogin(creds: AdminCredentials): Promise<boolean> {
    const resp = await fetch(API_BASE_URL + '/admin_page', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(creds),
    });
    if (resp.ok) {
        storeAdminCredentials(creds);
        return true;
    }
    return false;
}

export async function setState(targetState: number) {
    return adminPost<SetStateResult>('/set_state', { targetState });
}

export async function getLeadingAuctionBid() {
    return adminPost<AuctionBidResult & { message: string }>('/get_leading_auction_bid');
}

export async function terminateAuction() {
    return adminPost<AuctionBidResult & { message: string }>('/terminate_auction');
}

export async function fetchLeaderboard() {
    return adminPostText('/leaderboard');
}

export async function shutdownServer() {
    return adminPost<{ message: string }>('/shutdown');
}

export async function setPrices(prices: Record<string, number>) {
    return adminPost<{ message: string }>('/set_price', { prices });
}

export async function setTickers(tickers: string[]) {
    return adminPost<SetTickersResult>('/set_tickers', { tickers });
}

export async function setMaxOrderPrice(maxPrice: number) {
    return adminPost<SetMaxOrderPriceResult>('/set_max_order_price', { maxPrice });
}

export async function sendUserWsMessage(targetUsername: string, message: string) {
    return adminPost<SendUserWsMessageResult>('/send_user_ws_message', {
        targetUsername,
        message,
    });
}

export async function saveRound(roundName: string, c?: number) {
    return adminPost<{ message: string; round: any }>('/save_round', { roundName, c });
}

export async function clearLeaderboardHistory() {
    return adminPost<{ message: string }>('/clear_leaderboard_history');
}

export async function reloadLeaderboardHistory() {
    return adminPost<{ message: string }>('/reload_leaderboard_history');
}

export interface TeamPnl {
    username: string;
    pnl: number;
    /** Per-round rank (1 = best). 0 for bots. Omitted in old data; treat as 0. */
    score?: number;
    /** True if trading bot; excluded from ranking and shown separately. */
    bot?: boolean;
}

export interface RoundResult {
    roundName: string;
    timestamp: number;
    results: TeamPnl[];
    /** Round constant C for scoring. Omitted in old data; default 1.0. */
    c?: number;
}

export interface LeaderboardHistoryData {
    rounds: RoundResult[];
}

export async function fetchLeaderboardHistory(): Promise<LeaderboardHistoryData | null> {
    try {
        const resp = await fetch(API_BASE_URL + '/leaderboard_history');
        if (resp.ok) {
            return await resp.json();
        }
    } catch {
        // Server unreachable
    }
    return null;
}

export async function fetchServerState(): Promise<number | null> {
    try {
        const resp = await fetch(API_BASE_URL + '/get_state');
        if (resp.ok) {
            const json = await resp.json();
            return typeof json.state === 'number' ? json.state : null;
        }
    } catch {
        // Server unreachable
    }
    return null;
}
